import React, {useEffect, useMemo, useState} from 'react'
import {
    Alert,
    Box,
    Card,
    CardContent,
    Typography
} from '@mui/material'
import {useLocation, useNavigate} from 'react-router-dom'
import {useAuth} from '../../auth/auth'
import {RouteSecurity} from '../../common/RouteSecurity'
import {webEvents} from '../../common/WebEvents'
import CheckIcon from '@mui/icons-material/Check'
import {SearchTransferResult, searchTransferRequests} from '../transferUtils'
import {useDispatch} from 'react-redux'
import {createError} from '../../store/slices/error'

type SearchTransferPayload = {
    to_locations?: string,
    from_locations?: string,
    statuses: string,
    page_size?: number
}


function TransferDashboard() {
    const {getPermissions, getTruck} = useAuth()
    let routeSecurity = new RouteSecurity(getPermissions())
    let truckName = getTruck()
    const location = useLocation()
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const [alertOpen, setAlertOpen] = useState(false)
    const [requestsInReview, setRequestsInReview] = useState<SearchTransferResult[]>([])
    const [pendingSelectionData, setPendingSelectionData] = useState<SearchTransferResult[]>([])
    const [rmDashboardData, setRmDashboardData] = useState<SearchTransferResult[]>([])

    const totalPendingSelectionRM = useMemo(() => {
        if(rmDashboardData) {
            return rmDashboardData.filter(item => item.status === 'pending-selection').length
        }
    }, [rmDashboardData])

    const getTransferRequests = (payload: SearchTransferPayload, userType: string) => {
        searchTransferRequests(payload)
            .then((t: SearchTransferResult[]) => {
                if (t.length) {
                    if (userType === 'tech') {
                        if (payload.statuses === 'in-review') {
                            setRequestsInReview(t)
                        }
                        if (payload.statuses === 'pending-selection') {
                            setPendingSelectionData(t)
                        }
                    } else if (userType === 'rm') {
                        setRmDashboardData(t)
                    }
                }
            })
            .catch(error => {
                const causeMessage = error.cause?.messages?.[0] || error.messages?.[0] || "Unknown error"
                webEvents.error(
                    causeMessage,
                    {'Fetching transfer Request': ''},
                    error.stack
                )
                dispatch(createError({
                    type: error.type,
                    message: causeMessage
                }))
            })
    }

    const isTechAuthorized = routeSecurity.isAuthorized('/inventory/transfer-requests')
    const isRmAuthorized = routeSecurity.isAuthorized('/transfer-requests/manage')

    useEffect(() => {
        const submissionSuccessItem = window.sessionStorage.getItem('submissionSuccess')
        if (submissionSuccessItem === 'true') {
            setAlertOpen(true)
            window.sessionStorage.setItem('submissionSuccess', 'false')
            return
        }
        if (isTechAuthorized || isRmAuthorized) {
            const inReviewPayload = {to_locations: truckName, statuses: 'in-review', page_size: 100}
            const pendingSelectionPayload = {from_locations: truckName, statuses: 'pending-selection', page_size: 100}
            getTransferRequests(inReviewPayload, 'tech')
            getTransferRequests(pendingSelectionPayload, 'tech')
        }
        if (isRmAuthorized) {
            const rmDashboardPayload = {statuses: 'in-review,pending-selection', page_size: 1000}
            getTransferRequests(rmDashboardPayload, 'rm')
        }
        return () => {
            window.sessionStorage.setItem('submissionSuccess', 'false')
        }
    }, [location.state])

    const navigateToDashboard = (userType: string) => {
        if (userType === 'rm') {
            navigate('/transfer-requests/manage', {
                state: {
                    dashboardData: rmDashboardData
                }
            })
        } else {
            navigate('/inventory/transfer-requests', {
                state: {
                    requestsInReview: requestsInReview,
                    pendingSelectionData: pendingSelectionData
                }
            })
        }
    }

    return (
        <Box display="flex" flexDirection="column" alignItems="center" p={3} gap={2}>
            {alertOpen && (
                <Alert
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        width: '80%',
                        mx: 'auto',
                        alignItems: 'flex-start',
                        my: 1
                    }}
                    icon={<CheckIcon fontSize="inherit"/>} severity="success"
                    onClose={() => setAlertOpen(false)}>
                    Report successfully submitted for review!
                </Alert>
            )}
            <Typography variant="h4" gutterBottom marginTop={3}>Transfer Requests Dashboard</Typography>
            <Box display="flex" flexWrap="wrap" alignItems="center" justifyContent="center" gap={3} p={3}
                 sx={{mx: 'auto', width: 'full'}}>
                <Card onClick={() => navigateToDashboard('tech')} sx={{
                    width: 350,
                    height: 350,
                    '&:hover': {
                        backgroundColor: 'whitesmoke',
                        border: 3,
                        borderColor: 'primary.main'
                    },
                    borderRadius: 2,
                    overflow: 'auto'
                }}>
                    <CardContent>
                        <Typography variant="h5" component="div">Create Request</Typography>
                        <Typography variant="h6" sx={{mt: 1.5}} color="text.secondary">
                            View Requests: {requestsInReview.length}
                        </Typography>
                        <Typography variant="h6" sx={{mt: 1.5}} color="text.secondary">
                            Selections Required: {pendingSelectionData.length}
                        </Typography>
                    </CardContent>
                </Card>

                {routeSecurity.isAuthorized('/transfer-requests/manage') && (
                    <Card onClick={() => navigateToDashboard('rm')}
                          sx={{
                              width: 350,
                              height: 350,
                              '&:hover': {
                                  backgroundColor: 'whitesmoke',
                                  border: 3,
                                  borderColor: 'primary.main'
                              },
                              borderRadius: 2,
                              overflow: 'auto'
                          }}>
                        <CardContent>
                            <Typography variant="h5" component="div">Manage</Typography>
                            <Typography variant="h6" sx={{mt: 1.5}} color="text.secondary">
                                In-Review: {rmDashboardData.length}
                            </Typography>
                            <Typography variant="h6" sx={{mt: 1.5}} color="text.secondary">
                                Pending Selection: {totalPendingSelectionRM}
                            </Typography>
                        </CardContent>
                    </Card>
                )}
            </Box>
        </Box>
    )
}

export default TransferDashboard
